import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Plus, ChevronRight, ChevronLeft, Send, MessageSquare, Users, CheckSquare, FileText, Bell, BookOpen, AlertTriangle, Trash2, Maximize2, Minimize2, GripVertical, Search, Archive, Star, Loader2, RefreshCw, X, PanelLeftClose, PanelLeft, ExternalLink, Download, Copy, Sparkles, Eye, Pencil, Save, Type, Hash, Clock, AlignLeft, HelpCircle, Target, FolderOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAIPanel } from '../context/AIPanelContext';
import AITaskGenerator from '../components/Tasks/AITaskGenerator';
import AINoticeGenerator from '../components/Notices/AINoticeGenerator';
import AICourseGenerator from '../components/Courses/AICourseGenerator';
import AISurveyGenerator from '../components/Surveys/AISurveyGenerator';

// Markdown prose styles for document rendering
const markdownComponents = {
    h1: ({ children }) => <h1 className="text-2xl font-bold text-gray-900 mb-4 mt-6 pb-2 border-b border-gray-200">{children}</h1>,
    h2: ({ children }) => <h2 className="text-xl font-semibold text-gray-800 mb-3 mt-5">{children}</h2>,
    h3: ({ children }) => <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-4">{children}</h3>,
    h4: ({ children }) => <h4 className="text-base font-semibold text-gray-700 mb-2 mt-3">{children}</h4>,
    p: ({ children }) => <p className="text-gray-700 leading-relaxed mb-3">{children}</p>,
    ul: ({ children }) => <ul className="list-disc list-inside space-y-1.5 mb-3 text-gray-700 ml-2">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-inside space-y-1.5 mb-3 text-gray-700 ml-2">{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    blockquote: ({ children }) => <blockquote className="border-l-4 border-primary/30 bg-primary/5 pl-4 py-2 my-3 text-gray-600 italic rounded-r-lg">{children}</blockquote>,
    code: ({ inline, children, ...props }) => inline
        ? <code className="bg-gray-100 text-primary px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
        : <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg my-3 overflow-x-auto text-sm font-mono"><code {...props}>{children}</code></pre>,
    table: ({ children }) => <div className="overflow-x-auto my-3"><table className="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden">{children}</table></div>,
    thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
    th: ({ children }) => <th className="border border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700">{children}</th>,
    td: ({ children }) => <td className="border border-gray-200 px-3 py-2 text-sm text-gray-600">{children}</td>,
    a: ({ children, href }) => <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
    hr: () => <hr className="my-4 border-gray-200" />,
    strong: ({ children }) => <strong className="font-semibold text-gray-800">{children}</strong>,
    em: ({ children }) => <em className="italic text-gray-600">{children}</em>,
};

// Compact markdown styles for chat messages
const chatMarkdownComponents = {
    ...markdownComponents,
    h1: ({ children }) => <h1 className="text-lg font-bold text-gray-900 mb-2 mt-3 pb-1 border-b border-gray-200">{children}</h1>,
    h2: ({ children }) => <h2 className="text-base font-semibold text-gray-800 mb-2 mt-3">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-semibold text-gray-800 mb-1.5 mt-2">{children}</h3>,
    p: ({ children }) => <p className="text-sm text-gray-700 leading-relaxed mb-2">{children}</p>,
    ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mb-2 text-sm text-gray-700 ml-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 mb-2 text-sm text-gray-700 ml-1">{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed text-sm">{children}</li>,
    code: ({ inline, children, ...props }) => inline
        ? <code className="bg-gray-100 text-primary px-1 py-0.5 rounded text-xs font-mono">{children}</code>
        : <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg my-2 overflow-x-auto text-xs font-mono"><code {...props}>{children}</code></pre>,
};

// ============================================
// Admin AI Assistant Page
// ============================================
// Features:
// - Light theme matching Employee AI
// - Sidebar with conversation history
// - Resizable chat/editor split
// - AI Data Editor (hidden by default)
// - Collapsible editor panel
// - Fixed resize handle (no text selection)
// ============================================

// Data Display Components — Premium Enterprise Design

const STATUS_STYLES = {
    active: 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/20',
    published: 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/20',
    completed: 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/20',
    onboarding: 'bg-sky-500/10 text-sky-600 ring-sky-500/20',
    preboarding: 'bg-amber-500/10 text-amber-600 ring-amber-500/20',
    pending: 'bg-amber-500/10 text-amber-600 ring-amber-500/20',
    in_progress: 'bg-sky-500/10 text-sky-600 ring-sky-500/20',
    draft: 'bg-slate-500/10 text-slate-500 ring-slate-500/20',
    inactive: 'bg-rose-500/10 text-rose-500 ring-rose-500/20',
    expired: 'bg-rose-500/10 text-rose-500 ring-rose-500/20',
    overdue: 'bg-rose-500/10 text-rose-500 ring-rose-500/20',
};

const PRIORITY_COLORS = {
    urgent: 'bg-rose-500', high: 'bg-orange-500', medium: 'bg-amber-400', normal: 'bg-sky-400', low: 'bg-emerald-400',
};

const StatusBadge = ({ status }) => status ? (
    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider rounded-md ring-1 ring-inset ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
        {status.replace(/_/g, ' ')}
    </span>
) : null;

const InfoRow = ({ icon: Icon, label, value, className = '' }) => value ? (
    <div className={`flex items-center gap-2 text-xs ${className}`}>
        {Icon && <Icon size={12} className="text-muted-foreground/60 shrink-0" />}
        <span className="text-muted-foreground/80">{label}</span>
        <span className="text-foreground font-medium ml-auto truncate max-w-[140px]">{value}</span>
    </div>
) : null;

const EmployeeCard = ({ employee }) => {
    const initials = `${employee.first_name?.[0] || ''}${employee.last_name?.[0] || ''}`.toUpperCase();
    const stageColor = {
        active: 'from-emerald-500 to-teal-500', onboarding: 'from-sky-500 to-blue-500',
        preboarding: 'from-amber-500 to-orange-500', inactive: 'from-slate-400 to-slate-500',
    }[employee.lifecycle_stage] || 'from-primary to-primary';

    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5 group">
            <div className={`h-1 bg-gradient-to-r ${stageColor}`} />
            <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                    <div className={`w-11 h-11 min-w-[44px] bg-gradient-to-br ${stageColor} rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-sm`}>
                        {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="m-0 text-sm font-semibold text-foreground leading-tight">{employee.first_name} {employee.last_name}</h4>
                        <p className="m-0 text-xs text-muted-foreground truncate mt-0.5">{employee.email}</p>
                    </div>
                    <StatusBadge status={employee.lifecycle_stage} />
                </div>
                <div className="space-y-1.5 pt-3 border-t border-border/50">
                    <InfoRow icon={Users} label="Role" value={employee.position} />
                    <InfoRow icon={Users} label="Department" value={employee.department} />
                    {employee.start_date && (
                        <InfoRow icon={CheckSquare} label="Started" value={new Date(employee.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
                    )}
                    {employee.phone && <InfoRow icon={MessageSquare} label="Phone" value={employee.phone} />}
                </div>
            </div>
        </div>
    );
};

const TaskCard = ({ task }) => (
    <div className="bg-card border border-border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5 group">
        <div className="p-4">
            <div className="flex items-start gap-3 mb-2">
                <div className="w-9 h-9 min-w-[36px] bg-primary/10 rounded-lg flex items-center justify-center">
                    <CheckSquare size={16} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="m-0 text-sm font-semibold text-foreground leading-tight">{task.title}</h4>
                    {task.description && (
                        <p className="m-0 text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">{task.description.substring(0, 100)}{task.description.length > 100 ? '…' : ''}</p>
                    )}
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
                <StatusBadge status={task.status} />
                {task.task_type && (
                    <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-md bg-primary/5 text-primary ring-1 ring-inset ring-primary/10 capitalize">{task.task_type}</span>
                )}
                {task.priority && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-muted text-muted-foreground capitalize">
                        <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_COLORS[task.priority] || 'bg-slate-400'}`}></span>
                        {task.priority}
                    </span>
                )}
            </div>
            <div className="space-y-1.5 pt-3 mt-3 border-t border-border/50">
                {task.phase && <InfoRow icon={BookOpen} label="Phase" value={task.phase} className="capitalize" />}
                {task.category && <InfoRow icon={FileText} label="Category" value={task.category} />}
                {task.estimated_duration && <InfoRow icon={CheckSquare} label="Duration" value={`${task.estimated_duration} min`} />}
                <div className="flex gap-2 mt-1">
                    {task.requires_proof && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 ring-1 ring-inset ring-violet-200">Proof Required</span>}
                    {task.passing_score && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-200">Pass: {task.passing_score}%</span>}
                </div>
            </div>
        </div>
    </div>
);

const FileCard = ({ file }) => {
    const ext = file.file_name?.split('.').pop()?.toLowerCase() || '';
    const typeColor = { pdf: 'text-red-500 bg-red-50', doc: 'text-blue-500 bg-blue-50', docx: 'text-blue-500 bg-blue-50', xls: 'text-green-500 bg-green-50', xlsx: 'text-green-500 bg-green-50', png: 'text-purple-500 bg-purple-50', jpg: 'text-purple-500 bg-purple-50' }[ext] || 'text-slate-500 bg-slate-50';

    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5 group">
            <div className="p-4">
                <div className="flex items-start gap-3 mb-2">
                    <div className={`w-9 h-9 min-w-[36px] rounded-lg flex items-center justify-center ${typeColor}`}>
                        <FileText size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="m-0 text-sm font-semibold text-foreground leading-tight">{file.file_name}</h4>
                        <p className="m-0 text-xs text-muted-foreground mt-0.5 uppercase tracking-wider">{ext} file</p>
                    </div>
                </div>
                <div className="space-y-1.5 pt-3 border-t border-border/50">
                    {file.file_category && <InfoRow icon={FileText} label="Category" value={file.file_category} className="capitalize" />}
                    {file.phase && <InfoRow icon={BookOpen} label="Phase" value={file.phase} className="capitalize" />}
                    {file.description && <p className="m-0 text-xs text-muted-foreground mt-2 line-clamp-2">{file.description}</p>}
                </div>
            </div>
        </div>
    );
};

const NoticeCard = ({ notice }) => (
    <div className="bg-card border border-border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5 group">
        <div className={`h-1 ${PRIORITY_COLORS[notice.priority] || 'bg-slate-300'}`} />
        <div className="p-4">
            <div className="flex items-start gap-3 mb-2">
                <div className="w-9 h-9 min-w-[36px] bg-amber-50 rounded-lg flex items-center justify-center">
                    <Bell size={16} className="text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="m-0 text-sm font-semibold text-foreground leading-tight">{notice.title}</h4>
                    {notice.content && <p className="m-0 text-xs text-muted-foreground mt-1 line-clamp-2">{notice.content.substring(0, 80)}</p>}
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
                <StatusBadge status={notice.status} />
                {notice.priority && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-muted text-muted-foreground capitalize">
                        <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_COLORS[notice.priority] || 'bg-slate-400'}`}></span>
                        {notice.priority}
                    </span>
                )}
            </div>
        </div>
    </div>
);

const CourseCard = ({ course }) => (
    <div className="bg-card border border-border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5 group">
        <div className="h-1 bg-gradient-to-r from-indigo-500 to-purple-500" />
        <div className="p-4">
            <div className="flex items-start gap-3 mb-2">
                <div className="w-9 h-9 min-w-[36px] bg-indigo-50 rounded-lg flex items-center justify-center">
                    <BookOpen size={16} className="text-indigo-500" />
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="m-0 text-sm font-semibold text-foreground leading-tight">{course.name}</h4>
                    {course.course_type && <p className="m-0 text-xs text-muted-foreground mt-0.5 capitalize">{course.course_type.replace(/_/g, ' ')}</p>}
                </div>
                <StatusBadge status={course.status} />
            </div>
            <div className="space-y-1.5 pt-3 border-t border-border/50">
                {course.duration_days && <InfoRow icon={CheckSquare} label="Duration" value={`${course.duration_days} days`} />}
                {course.phase && <InfoRow icon={BookOpen} label="Phase" value={course.phase} className="capitalize" />}
                {course.auto_enrollment_enabled && (
                    <span className="inline-flex text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-200 mt-1">Auto-Enroll</span>
                )}
            </div>
        </div>
    </div>
);

// Detect data type and render appropriate cards
const DataDisplay = ({ data, dataType }) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <div className="w-16 h-16 bg-muted/50 rounded-2xl flex items-center justify-center mb-4">
                    <MessageSquare size={28} strokeWidth={1.5} className="text-muted-foreground/40" />
                </div>
                <h3 className="text-base font-semibold text-foreground mb-1">No Data to Display</h3>
                <p className="text-sm text-muted-foreground max-w-xs">Ask the AI assistant to retrieve data and it will appear here.</p>
            </div>
        );
    }

    const firstItem = data[0];
    let detectedType = dataType || 'generic';

    // Auto-detect type
    if (!dataType) {
        if (firstItem.first_name || firstItem.last_name) detectedType = 'employee';
        else if (firstItem.title && (firstItem.task_type || firstItem.phase)) detectedType = 'task';
        else if (firstItem.file_name || firstItem.file_type) detectedType = 'file';
        else if (firstItem.title && firstItem.priority) detectedType = 'notice';
        else if (firstItem.name && (firstItem.course_type || firstItem.duration_days)) detectedType = 'course';
    }

    const META = {
        employee: { icon: Users, title: 'Employees', gradient: 'from-emerald-500 to-teal-500' },
        task: { icon: CheckSquare, title: 'Tasks', gradient: 'from-primary to-blue-600' },
        file: { icon: FileText, title: 'Files', gradient: 'from-violet-500 to-purple-500' },
        notice: { icon: Bell, title: 'Notices', gradient: 'from-amber-500 to-orange-500' },
        course: { icon: BookOpen, title: 'Courses', gradient: 'from-indigo-500 to-purple-500' },
        generic: { icon: MessageSquare, title: 'Results', gradient: 'from-slate-500 to-slate-600' },
    };

    const meta = META[detectedType] || META.generic;
    const Icon = meta.icon;

    return (
        <div className="animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-border">
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 bg-gradient-to-br ${meta.gradient} rounded-xl flex items-center justify-center text-white shadow-lg`}>
                        <Icon size={18} />
                    </div>
                    <div>
                        <h3 className="m-0 text-base font-semibold text-foreground">{meta.title}</h3>
                        <span className="text-xs text-muted-foreground">{data.length} item{data.length !== 1 ? 's' : ''} found</span>
                    </div>
                </div>
                <span className={`px-3 py-1.5 text-xs font-bold rounded-xl bg-gradient-to-r ${meta.gradient} text-white shadow-sm`}>
                    {data.length}
                </span>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                {data.map((item, index) => {
                    const style = { animationDelay: `${index * 50}ms` };
                    switch (detectedType) {
                        case 'employee':
                            return <div key={item.id || index} className="animate-fade-in" style={style}><EmployeeCard employee={item} /></div>;
                        case 'task':
                            return <div key={item.id || index} className="animate-fade-in" style={style}><TaskCard task={item} /></div>;
                        case 'file':
                            return <div key={item.id || index} className="animate-fade-in" style={style}><FileCard file={item} /></div>;
                        case 'notice':
                            return <div key={item.id || index} className="animate-fade-in" style={style}><NoticeCard notice={item} /></div>;
                        case 'course':
                            return <div key={item.id || index} className="animate-fade-in" style={style}><CourseCard course={item} /></div>;
                        default:
                            return (
                                <div key={index} className="bg-card border border-border rounded-xl p-4 overflow-hidden animate-fade-in" style={style}>
                                    <pre className="m-0 text-xs whitespace-pre-wrap break-all text-muted-foreground font-mono leading-relaxed">{JSON.stringify(item, null, 2)}</pre>
                                </div>
                            );
                    }
                })}
            </div>
        </div>
    );
};

// Creator route mapping
const CREATOR_ROUTES = {
    task: '/admin/activity-centre/tasks',
    course: '/admin/activity-centre/courses',
    notice: '/admin/activity-centre/notices',
    survey: '/admin/activity-centre/surveys',
    compliance: '/admin/activity-centre/compliance',
};

const CREATOR_LABELS = {
    task: 'Task Creator',
    course: 'Course Creator',
    notice: 'Notice Creator',
    survey: 'Survey Creator',
    compliance: 'Compliance Manager',
};

// Generated File Editor - Professional with Edit/Preview toggle
const GeneratedFileEditor = ({ generatedFile, setGeneratedFile }) => {
    const [editorMode, setEditorMode] = useState('preview');
    const [editContent, setEditContent] = useState(generatedFile?.content || '');
    const [hasEdited, setHasEdited] = useState(false);

    useEffect(() => {
        setEditContent(generatedFile?.content || '');
        setHasEdited(false);
    }, [generatedFile?.content]);

    const content = hasEdited ? editContent : generatedFile?.content || '';

    const stats = useMemo(() => {
        const text = content || '';
        const words = text.trim() ? text.trim().split(/\s+/).length : 0;
        const chars = text.length;
        const readingTime = Math.max(1, Math.ceil(words / 200));
        return { words, chars, readingTime };
    }, [content]);

    return (
        <div className="animate-fade-in flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center gap-3 mb-0 pb-3 border-b border-border">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="m-0 text-base font-bold text-foreground truncate">{generatedFile.title}</h3>
                    <span className="text-xs text-muted-foreground capitalize">{generatedFile.file_type?.replace('_', ' ')} — {new Date(generatedFile.generated_at).toLocaleString()}</span>
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between py-2.5 border-b border-border">
                {/* Edit/Preview Toggle */}
                <div className="flex items-center p-0.5 bg-gray-100 rounded-lg border border-gray-200">
                    <button
                        onClick={() => setEditorMode('edit')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${editorMode === 'edit'
                            ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        <Pencil size={13} />
                        Edit
                    </button>
                    <button
                        onClick={() => setEditorMode('preview')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${editorMode === 'preview'
                            ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        <Eye size={13} />
                        Preview
                    </button>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                    <button
                        className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                        title="Copy to clipboard"
                        onClick={() => {
                            navigator.clipboard.writeText(content);
                            toast.success('Copied to clipboard');
                        }}
                    >
                        <Copy size={15} />
                    </button>
                    <button
                        className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                        title="Download"
                        onClick={() => {
                            const blob = new Blob([content], { type: 'text/markdown' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${generatedFile.title.replace(/\s+/g, '_')}.md`;
                            a.click();
                            URL.revokeObjectURL(url);
                            toast.success('File downloaded');
                        }}
                    >
                        <Download size={15} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto mt-3">
                {generatedFile.description && (
                    <p className="text-xs text-muted-foreground mb-3 italic px-1">{generatedFile.description}</p>
                )}

                {editorMode === 'edit' ? (
                    <textarea
                        value={editContent}
                        onChange={(e) => {
                            setEditContent(e.target.value);
                            setHasEdited(true);
                        }}
                        className="w-full h-full min-h-[400px] bg-white border border-gray-200 rounded-xl p-5 text-gray-800 text-sm leading-relaxed resize-none focus:outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/10 transition-all placeholder:text-gray-400 shadow-sm font-mono"
                        spellCheck={true}
                    />
                ) : (
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                        <div className="p-5 sm:p-6">
                            {content ? (
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                    {content}
                                </ReactMarkdown>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                                    <FileText size={36} strokeWidth={1.5} />
                                    <p className="mt-3 text-sm">No content yet</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer Stats */}
            <div className="flex items-center justify-between pt-2 mt-2 border-t border-border text-xs text-gray-400">
                <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5"><Type size={11} />{stats.words.toLocaleString()} words</span>
                    <span className="flex items-center gap-1.5"><Hash size={11} />{stats.chars.toLocaleString()} chars</span>
                    <span className="flex items-center gap-1.5"><Clock size={11} />{stats.readingTime} min read</span>
                </div>
                <span className="flex items-center gap-1.5"><AlignLeft size={11} />{editorMode === 'edit' ? 'Markdown' : 'Preview'}</span>
            </div>
        </div>
    );
};

const AdminAIPage = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [showConversationList, setShowConversationList] = useState(true);
    const [editorData, setEditorData] = useState(null);
    const [editorDataType, setEditorDataType] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
    const [generatedFile, setGeneratedFile] = useState(null);
    const [pendingRouting, setPendingRouting] = useState(null);

    // AI Generator modal state
    const [openGenerator, setOpenGenerator] = useState(null);
    const [generatorPrefilledData, setGeneratorPrefilledData] = useState({});

    // AI Panel Context (panels persist across navigation)
    const { openCreator, openAnalytics } = useAIPanel();

    // Editor panel state
    const [showEditor, setShowEditor] = useState(false);
    const [editorMinimized, setEditorMinimized] = useState(false);

    // Resizer state
    const [chatWidth, setChatWidth] = useState(50); // percentage
    const [isResizing, setIsResizing] = useState(false);
    const containerRef = useRef(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Rotating thinking messages for natural feel
    const thinkingMessages = [
        'Checking your onboarding data...',
        'Looking through your records...',
        'Searching your company data...',
        'Let me check on that...',
    ];
    const [thinkingIndex, setThinkingIndex] = useState(0);

    // Cycle through thinking messages while loading
    useEffect(() => {
        if (!isSending) return;
        setThinkingIndex(Math.floor(Math.random() * thinkingMessages.length));
        const interval = setInterval(() => {
            setThinkingIndex(prev => (prev + 1) % thinkingMessages.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [isSending]);

    const MIN_CHAT_WIDTH = 30; // minimum 30%
    const MAX_CHAT_WIDTH = 70; // maximum 70%

    // Fetch conversations on mount
    useEffect(() => {
        if (token) {
            fetchConversations();
        }
    }, [token]);

    // Scroll to bottom on new messages and state changes
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isSending, pendingAction, pendingRouting]);

    // Focus input when opening a conversation
    useEffect(() => {
        if (!showConversationList && activeConversation) {
            inputRef.current?.focus();
        }
    }, [showConversationList, activeConversation]);

    // Show editor when data is available
    useEffect(() => {
        if (editorData && Array.isArray(editorData) && editorData.length > 0) {
            setShowEditor(true);
            setEditorMinimized(false);
        }
    }, [editorData]);

    // Handle resize mouse events - FIXED to prevent text selection
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isResizing || !containerRef.current) return;

        e.preventDefault();
        const containerRect = containerRef.current.getBoundingClientRect();
        const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

        if (newWidth >= MIN_CHAT_WIDTH && newWidth <= MAX_CHAT_WIDTH) {
            setChatWidth(newWidth);
        }
    }, [isResizing]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            document.body.style.webkitUserSelect = 'none';
            document.body.style.msUserSelect = 'none';
        } else {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.body.style.webkitUserSelect = '';
            document.body.style.msUserSelect = '';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.body.style.webkitUserSelect = '';
            document.body.style.msUserSelect = '';
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    const fetchConversations = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`/admin/ai/conversations`);
            const data = response.data;
            if (data.success) {
                setConversations(data.conversations || []);
            }
        } catch (error) {
            console.error('Failed to fetch conversations:', error);
            // Don't show toast for initial load - might be empty
        } finally {
            setIsLoading(false);
        }
    };

    const createConversation = async () => {
        try {
            const response = await api.post(`/admin/ai/conversations`, { title: 'New Conversation' });
            const data = response.data;
            if (data.success) {
                setActiveConversation(data.conversation);
                setMessages([]);
                setEditorData(null);
                setShowEditor(false);
                setShowConversationList(false);
                fetchConversations();
            }
        } catch (error) {
            console.error('Create conversation error:', error);
            toast.error('Failed to create conversation. Please check the database setup.');
        }
    };

    const loadConversation = async (conversation) => {
        try {
            setIsLoading(true);
            const response = await api.get(`/admin/ai/conversations/${conversation.id}`);
            const data = response.data;
            if (data.success) {
                setActiveConversation(data.conversation);
                setMessages(data.messages || []);
                setEditorData(null);
                setShowEditor(false);
                setShowConversationList(false);

                // Find the last message with data and show it in editor
                const lastDataMessage = [...(data.messages || [])].reverse().find(m => m.rawData);
                if (lastDataMessage) {
                    setEditorData(lastDataMessage.rawData);
                    setShowEditor(true);
                }
            }
        } catch (error) {
            toast.error('Failed to load conversation');
        } finally {
            setIsLoading(false);
        }
    };

    const deleteConversation = async (e, conversationId) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this conversation?')) return;

        try {
            const response = await api.delete(`/admin/ai/conversations/${conversationId}`);
            if (response.data.success) {
                toast.success('Conversation deleted');
                fetchConversations();
                if (activeConversation?.id === conversationId) {
                    setActiveConversation(null);
                    setMessages([]);
                    setShowConversationList(true);
                }
            }
        } catch (error) {
            toast.error('Failed to delete conversation');
        }
    };

    const generateConversationTitle = (message) => {
        const cleanMessage = message.trim();
        if (cleanMessage.length <= 40) return cleanMessage;
        return cleanMessage.substring(0, 37) + '...';
    };

    const sendMessage = async (e) => {
        e?.preventDefault();
        if (!inputMessage.trim() || isSending || !activeConversation) return;

        const userMessage = inputMessage.trim();
        setInputMessage('');
        setIsSending(true);
        setPendingAction(null);

        // Add user message to UI immediately
        setMessages(prev => [...prev, { role: 'user', content: userMessage, created_at: new Date().toISOString() }]);

        try {
            const response = await api.post(`/admin/ai/conversations/${activeConversation.id}/messages`, { message: userMessage });
            const data = response.data;

            if (data.success) {
                // Auto-generate title from first message if still "New Conversation"
                if (activeConversation.title === 'New Conversation' && messages.length === 0) {
                    const newTitle = generateConversationTitle(userMessage);
                    try {
                        await api.patch(`/admin/ai/conversations/${activeConversation.id}/title`, { title: newTitle });
                        setActiveConversation(prev => ({ ...prev, title: newTitle }));
                        fetchConversations();
                    } catch (err) {
                        console.error('Failed to update title:', err);
                    }
                }

                // Add AI response
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: data.response.message,
                    intent: data.response.intent,
                    created_at: new Date().toISOString(),
                }]);

                // Handle action requiring confirmation
                if (data.requires_confirmation && data.plan) {
                    setPendingAction(data.plan);
                }

                // Handle executed action - show data in editor
                if (data.executed && data.execution_result) {
                    if (data.execution_result.status === 'success') {
                        const resultData = data.execution_result.data;
                        if (resultData && Array.isArray(resultData) && resultData.length > 0) {
                            setEditorData(resultData);
                            // Add message reference
                            setMessages(prev => [...prev, {
                                role: 'assistant',
                                content: `Found ${resultData.length} result${resultData.length !== 1 ? 's' : ''}. I've loaded the data in the editor panel for you.`,
                                intent: 'data',
                                isDataResult: true,
                                rawData: resultData,
                                created_at: new Date().toISOString(),
                            }]);
                        }
                    } else {
                        toast.error('Action failed');
                    }
                }

                // Handle permission denied
                if (data.permission_denied) {
                    toast.error('Permission denied');
                }

                // Handle routing to activity creator
                if (data.routing) {
                    setPendingRouting(data.routing);
                }

                // Handle file creation
                if (data.file) {
                    setGeneratedFile(data.file);
                    setShowEditor(true);
                    setEditorMinimized(false);
                }
            } else {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: data.message || 'Sorry, something went wrong.',
                    intent: 'error',
                    created_at: new Date().toISOString(),
                }]);
            }
        } catch (error) {
            console.error('Send message error:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: error.response?.data?.message || 'Failed to connect to AI service. Please check your database setup and try again.',
                intent: 'error',
                created_at: new Date().toISOString(),
            }]);
        } finally {
            setIsSending(false);
        }
    };

    const confirmAction = async (confirmed) => {
        if (!pendingAction) return;

        setIsSending(true);

        try {
            if (confirmed) {
                const response = await api.post(`/admin/ai/conversations/${activeConversation.id}/messages`, {
                    message: 'Confirm action',
                    confirm_action: true,
                    plan_id: pendingAction.plan_id,
                    action: pendingAction.action,
                });

                const data = response.data;

                if (data.success) {
                    setMessages(prev => [...prev, {
                        role: 'system',
                        content: `Action ${data.execution_result?.status === 'success' ? 'completed successfully' : 'failed'}`,
                        created_at: new Date().toISOString(),
                    }]);

                    if (data.execution_result?.status === 'success') {
                        toast.success('Action completed');
                        if (data.execution_result.data && Array.isArray(data.execution_result.data)) {
                            setEditorData(data.execution_result.data);
                        }
                    } else {
                        toast.error('Action failed');
                    }
                }
            } else {
                setMessages(prev => [...prev, {
                    role: 'system',
                    content: 'Action cancelled',
                    created_at: new Date().toISOString(),
                }]);
            }
        } catch (error) {
            toast.error('Failed to process action');
        } finally {
            setPendingAction(null);
            setIsSending(false);
        }
    };

    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const today = new Date();
        if (date.toDateString() === today.toDateString()) return 'Today';
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
        return date.toLocaleDateString();
    };

    const filteredConversations = conversations.filter(conv =>
        conv.title?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="flex h-[calc(100vh-104px)] bg-card overflow-hidden" ref={containerRef}>
            {/* Sidebar - Light Theme */}
            <aside className={`
                ${sidebarCollapsed ? 'w-[70px] min-w-[70px]' : 'w-[280px] min-w-[280px] max-w-[320px]'}
                bg-background flex flex-col border-r border-border transition-all duration-300 overflow-hidden
            `}>
                {/* Sidebar Header */}
                <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} p-4 border-b border-border`}>
                    {!sidebarCollapsed && (
                        <div className="flex items-center gap-2 text-primary font-bold">
                            <Bot size={20} />
                            <span>Admin AI</span>
                        </div>
                    )}
                    {sidebarCollapsed && (
                        <div className="flex items-center justify-center text-primary font-bold">
                            <Bot size={24} />
                        </div>
                    )}
                    <button
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        className={`p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-muted-foreground transition-colors ${sidebarCollapsed ? 'hidden' : ''}`}
                    >
                        <PanelLeftClose size={18} />
                    </button>
                </div>

                {/* Expand button when collapsed */}
                {sidebarCollapsed && (
                    <div className="flex items-center justify-center py-2">
                        <button
                            onClick={() => setSidebarCollapsed(false)}
                            className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground hover:text-muted-foreground transition-colors"
                        >
                            <PanelLeft size={18} />
                        </button>
                    </div>
                )}

                {!sidebarCollapsed && (
                    <>
                        {/* New Conversation Button */}
                        <button
                            onClick={createConversation}
                            className="flex items-center justify-center gap-2 mx-4 mt-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-all shadow-sm hover:shadow-lg hover:shadow-primary/20"
                        >
                            <Plus size={18} />
                            <span>New Conversation</span>
                        </button>

                        {/* Search */}
                        <div className="flex items-center gap-2 mx-4 mt-4 mb-3 px-3 py-2 bg-card rounded-lg border border-border shadow-sm focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                            <Search size={16} className="text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Search conversations..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="flex-1 bg-transparent border-none text-sm text-foreground placeholder-gray-400 focus:outline-none"
                            />
                        </div>

                        {/* Conversation List */}
                        <div className="flex-1 overflow-y-auto px-2">
                            {isLoading && conversations.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                                    <Loader2 className="animate-spin" size={20} />
                                    <span className="mt-2 text-sm">Loading...</span>
                                </div>
                            ) : filteredConversations.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                                    <MessageSquare size={24} />
                                    <p className="mt-2 text-sm">No conversations yet</p>
                                    <p className="text-xs text-muted-foreground mt-1">Start a new chat to begin</p>
                                </div>
                            ) : (
                                filteredConversations.map((conv) => (
                                    <div
                                        key={conv.id}
                                        onClick={() => loadConversation(conv)}
                                        className={`
                                            flex items-center p-3 mb-1 rounded-xl cursor-pointer transition-all group
                                            ${activeConversation?.id === conv.id
                                                ? 'bg-primary/10 border-l-3 border-primary'
                                                : 'hover:bg-muted'}
                                        `}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className={`text-sm font-medium truncate ${activeConversation?.id === conv.id ? 'text-primary' : 'text-foreground'}`}>
                                                {conv.title}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                {formatDate(conv.updated_at)}
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => deleteConversation(e, conv.id)}
                                            className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 flex overflow-hidden">
                {/* Chat Area */}
                <div
                    className="flex flex-col bg-background overflow-hidden min-h-0 transition-all duration-300"
                    style={{ width: showEditor && !editorMinimized ? `${chatWidth}%` : '100%' }}
                >
                    {!activeConversation ? (
                        /* Welcome Screen - Light Theme */
                        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-card">
                            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                                <Bot size={48} className="text-primary" />
                            </div>
                            <h2 className="text-2xl font-bold text-foreground mb-3">Admin AI Assistant</h2>
                            <p className="max-w-md text-muted-foreground leading-relaxed mb-8">
                                I can help you manage employees, tasks, files, and more.
                                Ask me to perform actions or retrieve data for you.
                            </p>
                            <button
                                onClick={createConversation}
                                className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/30 transition-all"
                            >
                                <Plus size={18} />
                                Start a New Chat
                            </button>
                            <div className="flex flex-wrap gap-2 justify-center mt-8 max-w-lg">
                                <span className="px-4 py-2 bg-background border border-border rounded-lg text-sm text-muted-foreground">
                                    "Show pending tasks"
                                </span>
                                <span className="px-4 py-2 bg-background border border-border rounded-lg text-sm text-muted-foreground">
                                    "How many employees are onboarding?"
                                </span>
                                <span className="px-4 py-2 bg-background border border-border rounded-lg text-sm text-muted-foreground">
                                    "Show me my courses"
                                </span>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Chat Header - Light Theme */}
                            <div className="flex items-center justify-between px-4 py-3 bg-card/80 backdrop-blur-md border-b border-border sticky top-0 z-10">
                                <div className="flex items-center gap-3">
                                    {sidebarCollapsed && (
                                        <button
                                            onClick={() => setSidebarCollapsed(false)}
                                            className="p-2 hover:bg-muted rounded-lg text-muted-foreground"
                                        >
                                            <MessageSquare size={20} />
                                        </button>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <Bot size={18} className="text-primary" />
                                        </div>
                                        <span className="text-foreground font-bold truncate">
                                            {activeConversation.title}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {(editorData || generatedFile) && (
                                        <button
                                            onClick={() => {
                                                if (editorMinimized) {
                                                    setEditorMinimized(false);
                                                    setShowEditor(true);
                                                } else {
                                                    setShowEditor(!showEditor);
                                                }
                                            }}
                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${showEditor && !editorMinimized
                                                ? 'bg-primary/10 text-primary'
                                                : 'bg-muted text-muted-foreground hover:bg-secondary'
                                                }`}
                                        >
                                            <FileText size={16} />
                                            <span className="hidden sm:inline">Data Editor</span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Messages - Light Theme */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-5 flex flex-col scroll-smooth">
                                {messages.length === 0 && (
                                    <div className="flex-1 flex flex-col items-center justify-center h-full min-h-[400px] px-4 py-12">
                                        <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                                            <Sparkles size={28} className="text-primary" />
                                        </div>
                                        <h3 className="text-lg font-bold text-foreground mb-1">How can I help you?</h3>
                                        <p className="text-sm text-muted-foreground mb-6">Ask me anything or try one of these suggestions</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full max-w-lg">
                                            {[
                                                { icon: <Users size={16} className="text-blue-500" />, label: 'Show all onboarding employees', bg: 'bg-blue-50 hover:bg-blue-100 border-blue-100' },
                                                { icon: <CheckSquare size={16} className="text-purple-500" />, label: 'Show pending tasks', bg: 'bg-purple-50 hover:bg-purple-100 border-purple-100' },
                                                { icon: <FileText size={16} className="text-green-500" />, label: 'Generate a report', bg: 'bg-green-50 hover:bg-green-100 border-green-100' },
                                                { icon: <Bell size={16} className="text-amber-500" />, label: 'Show active notices', bg: 'bg-amber-50 hover:bg-amber-100 border-amber-100' },
                                            ].map((suggestion, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => {
                                                        setInputMessage(suggestion.label);
                                                        inputRef.current?.focus();
                                                    }}
                                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm text-gray-700 font-medium border transition-all ${suggestion.bg} hover:shadow-sm`}
                                                >
                                                    {suggestion.icon}
                                                    <span>{suggestion.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {messages.map((msg, index) => (
                                    <div
                                        key={index}
                                        className={`flex gap-3 max-w-[85%] ${msg.role === 'user' ? 'ml-auto flex-row-reverse' : ''}`}
                                    >
                                        {msg.role === 'assistant' && (
                                            <div className="w-8 h-8 min-w-[32px] bg-primary rounded-full flex items-center justify-center text-white shadow-sm">
                                                <Bot size={16} />
                                            </div>
                                        )}
                                        <div className={`px-4 py-3 rounded-2xl relative shadow-sm
                                            ${msg.role === 'user'
                                                ? 'bg-primary text-white rounded-br-md shadow-primary/20'
                                                : 'bg-card border border-border text-foreground rounded-bl-md'}
                                            ${msg.role === 'system' ? 'bg-background border border-border px-3 py-2 text-sm text-muted-foreground w-full text-center' : ''}
                                        `}>
                                            <div className="text-sm leading-relaxed break-words">
                                                {msg.role === 'assistant' ? (
                                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
                                                        {msg.content}
                                                    </ReactMarkdown>
                                                ) : (
                                                    <p className="m-0 whitespace-pre-wrap">{msg.content}</p>
                                                )}
                                            </div>
                                            <span className={`text-[10px] opacity-70 mt-1 block ${msg.role === 'user' ? 'text-white/70' : 'text-muted-foreground'}`}>
                                                {formatTime(msg.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                ))}

                                {/* Loading Indicator */}
                                {isSending && (
                                    <div className="flex gap-3 max-w-[85%]">
                                        <div className="w-8 h-8 min-w-[32px] bg-primary rounded-full flex items-center justify-center text-white">
                                            <Bot size={16} />
                                        </div>
                                        <div className="flex items-center gap-2 px-4 py-3 bg-card border border-border rounded-2xl rounded-bl-md shadow-sm">
                                            <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse"></div>
                                            <span className="text-sm text-muted-foreground italic">{thinkingMessages[thinkingIndex]}</span>
                                        </div>
                                    </div>
                                )}

                                <div ref={messagesEndRef} />
                            </div>

                            {/* Action Confirmation - Light Theme */}
                            {pendingAction && (
                                <div className="mx-4 mb-2 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                    <div className="flex items-center gap-2 mb-3 font-semibold text-amber-700">
                                        <AlertTriangle size={18} />
                                        <span>Confirm Action</span>
                                    </div>

                                    <div className="flex flex-col gap-2 mb-4">
                                        <div className="text-sm text-foreground">
                                            <strong className="capitalize text-foreground">{pendingAction.action?.operation}</strong> {pendingAction.action?.entity}
                                        </div>

                                        {pendingAction.preview?.affected_items?.length > 0 && (
                                            <div className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
                                                <span>Affected: </span>
                                                {pendingAction.preview.affected_items.map((item, i) => (
                                                    <span key={i} className="px-2.5 py-1 bg-card border border-border rounded-lg text-xs text-foreground">{item.display}</span>
                                                ))}
                                            </div>
                                        )}

                                        {pendingAction.destructive?.isDestructive && (
                                            <div className={`px-3 py-2.5 rounded-lg text-sm border
                                                ${pendingAction.destructive.level === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' : ''}
                                                ${pendingAction.destructive.level === 'high' || pendingAction.destructive.level === 'critical' ? 'bg-red-50 text-red-700 border-red-200' : ''}
                                            `}>
                                                ⚠️ {pendingAction.destructive.reasons?.[0] || 'This action is destructive'}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex gap-2.5">
                                        <button
                                            className="flex-1 px-4 py-2.5 bg-card hover:bg-background border border-border rounded-xl font-medium text-sm text-foreground cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                            onClick={() => confirmAction(false)}
                                            disabled={isSending}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-primary to-primary-hover border-none rounded-xl font-medium text-sm text-white cursor-pointer transition-all hover:shadow-lg hover:shadow-primary/20 disabled:opacity-60 disabled:cursor-not-allowed"
                                            onClick={() => confirmAction(true)}
                                            disabled={isSending}
                                        >
                                            {isSending ? 'Processing...' : 'Confirm'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Routing to Creator */}
                            {pendingRouting && (
                                <div className="mx-4 mb-2 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                                    <div className="flex items-center gap-2 mb-3 font-semibold text-blue-700">
                                        <ExternalLink size={18} />
                                        <span>{CREATOR_LABELS[pendingRouting.creator_type] || 'Activity Creator'}</span>
                                    </div>
                                    {pendingRouting.suggestion && (
                                        <p className="text-sm text-blue-600 mb-3">{pendingRouting.suggestion}</p>
                                    )}
                                    {pendingRouting.prefilled_data?.title && (
                                        <div className="text-sm text-foreground mb-3">
                                            Pre-filled: <span className="font-medium">{pendingRouting.prefilled_data.title}</span>
                                        </div>
                                    )}
                                    <div className="flex gap-2.5">
                                        <button
                                            className="flex-1 px-4 py-2.5 bg-card hover:bg-background border border-border rounded-xl font-medium text-sm text-foreground cursor-pointer transition-colors"
                                            onClick={() => setPendingRouting(null)}
                                        >
                                            Dismiss
                                        </button>
                                        <button
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary border-none rounded-xl font-medium text-sm text-white cursor-pointer transition-all hover:shadow-lg hover:shadow-primary/20"
                                            onClick={() => {
                                                setOpenGenerator(pendingRouting.creator_type);
                                                setGeneratorPrefilledData(pendingRouting.prefilled_data || {});
                                                setPendingRouting(null);
                                            }}
                                        >
                                            <Sparkles size={14} />
                                            Open {CREATOR_LABELS[pendingRouting.creator_type] || 'Creator'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Input - Light Theme */}
                            <form className="flex gap-3 p-4 bg-card/95 backdrop-blur-md border-t border-border shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.05)] z-10 sticky bottom-0" onSubmit={sendMessage}>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={inputMessage}
                                    onChange={(e) => setInputMessage(e.target.value)}
                                    placeholder="Ask me anything..."
                                    disabled={isSending || pendingAction}
                                    className="flex-1 px-5 py-3.5 bg-background border border-border/70 rounded-2xl text-sm text-foreground outline-none transition-all shadow-sm focus:border-primary/50 focus:ring-4 focus:ring-primary/10 placeholder:text-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                                <button
                                    type="submit"
                                    disabled={!inputMessage.trim() || isSending || pendingAction}
                                    className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-primary to-primary-hover border-none text-white cursor-pointer flex items-center justify-center transition-all shadow-md hover:scale-105 hover:shadow-lg hover:shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none shrink-0"
                                >
                                    {isSending ? <Loader2 className="animate-spin" size={20} /> : <Send size={18} className="translate-x-0.5" />}
                                </button>
                            </form>
                        </>
                    )}
                </div>

                {/* Resize Handle - Fixed to prevent text selection */}
                {showEditor && !editorMinimized && (
                    <div
                        onMouseDown={handleMouseDown}
                        className="w-4 hover:w-5 -ml-2 z-20 bg-transparent hover:bg-primary/10 cursor-col-resize flex items-center justify-center transition-all group relative select-none"
                    >
                        <div className="absolute inset-y-0 w-px bg-secondary group-hover:bg-primary/50 transition-colors" />
                        <div className="w-6 h-12 bg-card border border-border rounded-full flex items-center justify-center shadow-sm group-hover:border-primary/30 group-hover:shadow-md transition-all">
                            <GripVertical size={14} className="text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                    </div>
                )}

                {/* Editor Panel - Light Theme, Hidden by Default, Collapsible */}
                {showEditor && (
                    <div
                        className={`flex flex-col bg-card border-l border-border transition-all duration-300 overflow-hidden ${editorMinimized ? 'w-12 min-w-[48px]' : ''
                            }`}
                        style={{ width: editorMinimized ? '48px' : `${100 - chatWidth}%` }}
                    >
                        {editorMinimized ? (
                            /* Minimized State - Vertical Tab */
                            <div className="flex flex-col items-center py-4 h-full">
                                <button
                                    onClick={() => setEditorMinimized(false)}
                                    className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors mb-2"
                                    title="Expand Editor"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <div className="flex flex-col items-center gap-2 writing-mode-vertical text-xs text-muted-foreground font-medium tracking-wide">
                                    <FileText size={16} className="text-primary" />
                                    <span className="rotate-180" style={{ writingMode: 'vertical-rl' }}>Data Editor</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Editor Header */}
                                <div className="flex items-center justify-between px-4 py-3 bg-background border-b border-border min-h-[56px]">
                                    <div className="flex items-center gap-2">
                                        <FileText size={18} className="text-primary" />
                                        <span className="font-semibold text-foreground">AI Data Editor</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {(editorData || generatedFile) && (
                                            <button
                                                className="px-3 py-1.5 bg-card hover:bg-muted border border-border rounded-lg text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-all"
                                                onClick={() => { setEditorData(null); setGeneratedFile(null); }}
                                            >
                                                Clear
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setEditorMinimized(true)}
                                            className="p-1.5 hover:bg-muted rounded-lg text-muted-foreground hover:text-muted-foreground transition-colors"
                                            title="Minimize"
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowEditor(false);
                                                setEditorData(null);
                                            }}
                                            className="p-1.5 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-500 transition-colors"
                                            title="Close"
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                </div>

                                {/* Editor Content */}
                                <div className="flex-1 overflow-y-auto p-6 bg-background">
                                    {generatedFile ? (
                                        <GeneratedFileEditor
                                            generatedFile={generatedFile}
                                            setGeneratedFile={setGeneratedFile}
                                        />
                                    ) : (
                                        <DataDisplay data={editorData} dataType={editorDataType} />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </main>

            {/* AI Generator Modals — opened from chat routing */}
            <AITaskGenerator
                isOpen={openGenerator === 'task' || openGenerator === 'compliance'}
                onClose={() => { setOpenGenerator(null); setGeneratorPrefilledData({}); }}
                onSuccess={() => { setOpenGenerator(null); setGeneratorPrefilledData({}); toast.success('Task created successfully'); }}
                phase="onboarding"
                isComplianceMode={openGenerator === 'compliance'}
                prefilledData={generatorPrefilledData}
            />
            <AINoticeGenerator
                isOpen={openGenerator === 'notice'}
                onClose={() => { setOpenGenerator(null); setGeneratorPrefilledData({}); }}
                onSuccess={() => { setOpenGenerator(null); setGeneratorPrefilledData({}); toast.success('Notice created successfully'); }}
                phase="onboarding"
                prefilledData={generatorPrefilledData}
            />
            <AICourseGenerator
                isOpen={openGenerator === 'course'}
                onClose={() => { setOpenGenerator(null); setGeneratorPrefilledData({}); }}
                onSuccess={() => { setOpenGenerator(null); setGeneratorPrefilledData({}); toast.success('Course created successfully'); }}
                courseType="onboarding"
                prefilledData={generatorPrefilledData}
            />
            <AISurveyGenerator
                isOpen={openGenerator === 'survey'}
                onClose={() => { setOpenGenerator(null); setGeneratorPrefilledData({}); }}
                onSuccess={() => { setOpenGenerator(null); setGeneratorPrefilledData({}); toast.success('Survey created successfully'); }}
                prefilledData={generatorPrefilledData}
            />
        </div>
    );
};

export default AdminAIPage;
